import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, BookOpenIcon, UsersIcon, BarChart2Icon, SearchIcon, MessageCircleIcon, BrainIcon, LightbulbIcon, ChevronRightIcon, CheckCircleIcon, XIcon, LogOutIcon } from 'lucide-react';
const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<string>('');
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [name, setName] = useState('Edoardo');
  const [isTyping, setIsTyping] = useState(true);
  const [typedText, setTypedText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  // Text for each step
  const stepTexts = [`Ciao ${name}! Sono ALGA, la tua assistente AI personale per il percorso di formazione EIIS.`, 'Sono qui per rendere il tuo percorso formativo più efficace e personalizzato. Sarò al tuo fianco durante tutto il corso per aiutarti a raggiungere i tuoi obiettivi.', 'Ecco come posso aiutarti nel tuo percorso di apprendimento:', 'Per personalizzare la tua esperienza, vorrei conoscerti meglio. Quali sono i tuoi principali interessi nel campo della sostenibilità?', 'Ottimo! Qual è il tuo livello di esperienza in questi ambiti?', 'Quali sono i tuoi obiettivi principali per questo percorso formativo?', `Perfetto! Grazie per queste informazioni, ${name}. Ho personalizzato la tua esperienza in base alle tue preferenze. Sono entusiasta di accompagnarti in questo percorso formativo!`, `Benvenuto in EIIS, ${name}! Il tuo percorso di formazione personalizzato è pronto. Scopri tutto ciò che la piattaforma ha da offrirti.`];
  // Typing effect
  useEffect(() => {
    if (step <= 7) {
      setIsTyping(true);
      setTypedText('');
      const text = stepTexts[step];
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentIndex < text.length) {
          setTypedText(prev => prev + text[currentIndex]);
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          setIsTyping(false);
        }
      }, 12); // Reduced from 20ms to 12ms for faster typing
      return () => clearInterval(typingInterval);
    }
  }, [step]);
  // Scroll to center when step changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [step]);
  const handleCompleteOnboarding = () => {
    // In a real app, you would save the user preferences here
    navigate('/');
  };
  const handleSkipOnboarding = () => {
    navigate('/');
  };
  const handleLogout = () => {
    navigate('/login');
  };
  const handleInterestToggle = (interest: string) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(i => i !== interest));
    } else {
      setSelectedInterests([...selectedInterests, interest]);
    }
  };
  const handleGoalToggle = (goal: string) => {
    if (selectedGoals.includes(goal)) {
      setSelectedGoals(selectedGoals.filter(g => g !== goal));
    } else {
      setSelectedGoals([...selectedGoals, goal]);
    }
  };
  const nextStep = () => {
    setStep(step + 1);
  };
  const interests = [{
    id: 'sustainability',
    label: 'Sostenibilità'
  }, {
    id: 'innovation',
    label: 'Innovazione'
  }, {
    id: 'circular-economy',
    label: 'Economia Circolare'
  }, {
    id: 'esg',
    label: 'ESG'
  }, {
    id: 'renewable-energy',
    label: 'Energie Rinnovabili'
  }, {
    id: 'green-finance',
    label: 'Green Finance'
  }, {
    id: 'corporate-sustainability',
    label: 'Sostenibilità Aziendale'
  }, {
    id: 'impact-investing',
    label: 'Impact Investing'
  }];
  const goals = [{
    id: 'professional-growth',
    label: 'Crescita Professionale'
  }, {
    id: 'networking',
    label: 'Networking'
  }, {
    id: 'new-skills',
    label: 'Acquisire Nuove Competenze'
  }, {
    id: 'career-change',
    label: 'Cambiare Carriera'
  }, {
    id: 'certification',
    label: 'Ottenere Certificazioni'
  }, {
    id: 'stay-updated',
    label: 'Rimanere Aggiornato'
  }];
  const experienceLevels = [{
    id: 'beginner',
    label: 'Principiante'
  }, {
    id: 'intermediate',
    label: 'Intermedio'
  }, {
    id: 'advanced',
    label: 'Avanzato'
  }, {
    id: 'expert',
    label: 'Esperto'
  }];
  const capabilities = [{
    icon: <BookOpenIcon className="text-custom-green" data-id="element-1947" />,
    title: 'Supporto alle lezioni',
    description: 'Posso rispondere a domande sui materiali del corso e fornirti approfondimenti su ogni argomento.'
  }, {
    icon: <SearchIcon className="text-custom-green" data-id="element-1948" />,
    title: 'Ricerca intelligente',
    description: 'Posso cercare informazioni aggiuntive sulla sostenibilità e aiutarti a trovare risorse pertinenti.'
  }, {
    icon: <UsersIcon className="text-custom-green" data-id="element-1949" />,
    title: 'Connessioni professionali',
    description: 'Posso suggerirti connessioni con altri professionisti in base ai tuoi interessi e obiettivi.'
  }, {
    icon: <BarChart2Icon className="text-custom-green" data-id="element-1950" />,
    title: 'Monitoraggio progressi',
    description: 'Tengo traccia dei tuoi progressi nel corso e ti aiuto a identificare aree di miglioramento.'
  }, {
    icon: <LightbulbIcon className="text-custom-green" data-id="element-1951" />,
    title: 'Widget intelligenti',
    description: 'Creo widget personalizzati con riassunti, schemi e approfondimenti sui contenuti del corso.'
  }, {
    icon: <MessageCircleIcon className="text-custom-green" data-id="element-1952" />,
    title: 'Assistenza continua',
    description: 'Sono sempre disponibile per chattare e rispondere alle tue domande in qualsiasi momento.'
  }];
  return <div className="flex flex-col h-screen bg-black" data-id="element-1953">
      {/* Header - similar to AlgaChat header */}
      <div className="flex justify-between items-center p-4 px-6 md:px-8 border-b border-[#111111]" data-id="element-1954">
        <div className="flex items-center" data-id="element-1955">
          <div className="flex flex-col items-start" data-id="element-1956">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kYK3VqJh5ErppvW42Sa6wk/IMG_1178.png" alt="EIIS Logo" className="h-12 object-contain" data-id="element-1957" />
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-1958">
          {/* Progress indicator */}
          <div className="hidden md:flex items-center gap-3 mr-4" data-id="element-1959">
            <div className="w-48 bg-[#222222] rounded-full h-2" data-id="element-1960">
              <div className="bg-custom-green h-2 rounded-full transition-all duration-500" style={{
              width: `${step / 7 * 100}%`
            }} data-id="element-1961"></div>
            </div>
            <span className="text-sm text-gray-400" data-id="element-1962">
              {Math.round(step / 7 * 100)}%
            </span>
          </div>
          <button onClick={handleSkipOnboarding} className="py-2 px-4 rounded-lg text-sm text-gray-300 hover:bg-[#222222] transition-colors" data-id="element-1963">
            Salta
          </button>
          <button onClick={handleLogout} className="py-2 px-4 rounded-lg text-sm text-gray-300 hover:bg-[#222222] transition-colors flex items-center" data-id="element-1964">
            <LogOutIcon size={16} className="mr-2" data-id="element-1965" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content - similar to chat area */}
      <div className="flex-1 overflow-y-auto px-6 md:px-8 pb-6" data-id="element-1966">
        <div className="max-w-3xl mx-auto" data-id="element-1967">
          {/* ALGA avatar and intro - similar to AlgaChat welcome */}
          <div className="flex flex-col items-center pt-8 pb-6" data-id="element-1968">
            <div className="flex items-center relative" data-id="element-1969">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-pink-500 mb-4" data-id="element-1970">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-1971" />
              </div>
              {isTyping && <div className="bg-pink-500 rounded-full p-2 absolute -right-5 bottom-4" data-id="element-1972">
                  <div className="flex space-x-1" data-id="element-1973">
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" data-id="element-1974"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                  animationDelay: '0.2s'
                }} data-id="element-1975"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                  animationDelay: '0.4s'
                }} data-id="element-1976"></div>
                  </div>
                </div>}
            </div>
            <h1 className="text-2xl font-bold mb-1 text-center text-white" data-id="element-1977">
              ALGA
            </h1>
            <p className="text-white text-center text-sm max-w-lg" data-id="element-1978">
              Powered by EIIS Intelligence
            </p>
          </div>

          {/* Chat-like conversation flow */}
          <div className="space-y-6" ref={containerRef} data-id="element-1979">
            {/* ALGA message - similar to chat bubble */}
            <div className="flex justify-start" data-id="element-1980">
              <div className="w-10 h-10 rounded-full overflow-hidden border-3 border-pink-500 mr-3 flex-shrink-0" data-id="element-1981">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-1982" />
              </div>
              <div className="max-w-[80%] p-4 rounded-lg rounded-tl-none bg-[#171717] text-gray-100 border border-[#171717]" data-id="element-1983">
                {typedText}
              </div>
            </div>

            {/* Step-specific content */}
            {!isTyping && <div className="ml-13 pl-13" data-id="element-1984">
                {/* Step 0-2: Introduction */}
                {step === 2 && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 ml-13 animate-fadeIn" data-id="element-1985">
                    {capabilities.map((capability, index) => <div key={index} className="rounded-lg p-4 flex items-start border border-[#333333]" data-id="element-1986">
                        <div className="rounded-full p-3 mr-3 border border-[#333333]" data-id="element-1987">
                          {capability.icon}
                        </div>
                        <div data-id="element-1988">
                          <h3 className="font-medium text-white" data-id="element-1989">
                            {capability.title}
                          </h3>
                          <p className="text-sm mt-1 text-gray-400" data-id="element-1990">
                            {capability.description}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {/* Step 3: Interests */}
                {step === 3 && <div className="animate-fadeIn ml-13" data-id="element-1991">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-id="element-1992">
                      {interests.map(interest => <button key={interest.id} onClick={() => handleInterestToggle(interest.label)} className={`p-3 rounded-lg text-sm transition-all ${selectedInterests.includes(interest.label) ? 'bg-custom-green text-white' : 'border border-[#333333] text-gray-300 hover:border-gray-500'}`} data-id="element-1993">
                          {selectedInterests.includes(interest.label) && <CheckIcon size={16} className="inline mr-2" data-id="element-1994" />}
                          {interest.label}
                        </button>)}
                    </div>
                  </div>}

                {/* Step 4: Experience Level */}
                {step === 4 && <div className="animate-fadeIn ml-13" data-id="element-1995">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-id="element-1996">
                      {experienceLevels.map(level => <button key={level.id} onClick={() => setSelectedExperience(level.label)} className={`p-3 rounded-lg text-sm transition-all ${selectedExperience === level.label ? 'bg-custom-green text-white' : 'border border-[#333333] text-gray-300 hover:border-gray-500'}`} data-id="element-1997">
                          {selectedExperience === level.label && <CheckIcon size={16} className="inline mr-2" data-id="element-1998" />}
                          {level.label}
                        </button>)}
                    </div>
                  </div>}

                {/* Step 5: Goals */}
                {step === 5 && <div className="animate-fadeIn ml-13" data-id="element-1999">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4" data-id="element-2000">
                      {goals.map(goal => <button key={goal.id} onClick={() => handleGoalToggle(goal.label)} className={`p-3 rounded-lg text-sm transition-all ${selectedGoals.includes(goal.label) ? 'bg-custom-green text-white' : 'border border-[#333333] text-gray-300 hover:border-gray-500'}`} data-id="element-2001">
                          {selectedGoals.includes(goal.label) && <CheckIcon size={16} className="inline mr-2" data-id="element-2002" />}
                          {goal.label}
                        </button>)}
                    </div>
                  </div>}

                {/* Step 6: Confirmation */}
                {step === 6 && <div className="animate-fadeIn ml-13" data-id="element-2003">
                    <div className="rounded-lg p-6 border border-[#333333]" data-id="element-2004">
                      <h3 className="text-lg font-medium mb-4 text-white" data-id="element-2005">
                        Il tuo profilo:
                      </h3>
                      <div className="space-y-4" data-id="element-2006">
                        <div data-id="element-2007">
                          <p className="text-gray-400 mb-2" data-id="element-2008">Interessi:</p>
                          <div className="flex flex-wrap gap-2" data-id="element-2009">
                            {selectedInterests.map((interest, idx) => <span key={idx} className="px-3 py-1 rounded-full text-sm border border-[#333333] text-white" data-id="element-2010">
                                {interest}
                              </span>)}
                          </div>
                        </div>
                        <div data-id="element-2011">
                          <p className="text-gray-400 mb-2" data-id="element-2012">
                            Livello di esperienza:
                          </p>
                          <span className="px-3 py-1 rounded-full text-sm border border-[#333333] text-white" data-id="element-2013">
                            {selectedExperience}
                          </span>
                        </div>
                        <div data-id="element-2014">
                          <p className="text-gray-400 mb-2" data-id="element-2015">Obiettivi:</p>
                          <div className="flex flex-wrap gap-2" data-id="element-2016">
                            {selectedGoals.map((goal, idx) => <span key={idx} className="px-3 py-1 rounded-full text-sm border border-[#333333] text-white" data-id="element-2017">
                                {goal}
                              </span>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Step 7: Welcome Page */}
                {step === 7 && <div className="flex flex-col items-center animate-fadeIn ml-13" data-id="element-2018">
                    <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6 border border-[#333333]" data-id="element-2019">
                      <CheckCircleIcon className="text-custom-green w-14 h-14" data-id="element-2020" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4 text-center text-white" data-id="element-2021">
                      Configurazione completata!
                    </h2>
                    <p className="text-center mb-8 max-w-lg text-gray-400" data-id="element-2022">
                      Tutto è pronto per iniziare il tuo percorso formativo
                      personalizzato. ALGA ti accompagnerà in ogni fase,
                      fornendoti supporto e risorse su misura per te.
                    </p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>

      {/* Footer with action button - similar to chat input area */}
      <div className="border-t p-4 border-[#111111] bg-black" data-id="element-2023">
        <div className="max-w-3xl mx-auto" data-id="element-2024">
          {!isTyping && <>
              {step < 7 ? <button onClick={nextStep} disabled={step === 3 && selectedInterests.length === 0 || step === 4 && !selectedExperience || step === 5 && selectedGoals.length === 0} className="w-full py-3 px-4 bg-custom-green text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-opacity-90" data-id="element-2025">
                  {step === 0 || step === 1 || step === 2 ? 'Continua' : step === 3 ? selectedInterests.length === 0 ? 'Seleziona almeno un interesse' : 'Continua' : step === 4 ? !selectedExperience ? 'Seleziona il tuo livello' : 'Continua' : step === 5 ? selectedGoals.length === 0 ? 'Seleziona almeno un obiettivo' : 'Continua' : 'Continua'}
                  <ChevronRightIcon size={18} className="ml-2" data-id="element-2026" />
                </button> : <button onClick={handleCompleteOnboarding} className="w-full py-3 px-4 bg-custom-green text-white rounded-full flex items-center justify-center transition-all hover:bg-opacity-90" data-id="element-2027">
                  Vai alla Dashboard
                  <ArrowRightIcon size={18} className="ml-2" data-id="element-2028" />
                </button>}
            </>}
        </div>
      </div>
    </div>;
};
export default Onboarding;